import { useCallback, useEffect, useState } from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import {
  adminUnpublishProject,
  getPublicProject,
  getProject,
  postProjectView,
  postProjectContactClick,
  reactToProject,
  withdrawProject,
  getApiErrorInfo,
  isModeratorOrAdmin,
} from '../lib/api';
import type {
  PublicProject,
  ApiErrorInfo,
  ProjectDetails,
  ProjectDetailRow,
  ShowcaseProjectsResponse,
} from '../lib/api';
import {
  getDisplayProjectMetaLabel,
  getProjectStageLabel,
  isProjectStageValue,
} from '../lib/projectMeta';
import { useAppStore } from '../store/useAppStore';
import { useBackButton } from '../hooks/useBackButton';
import { useDisableSwipes } from '../hooks/useSwipeBehavior';
import { useMainButton } from '../hooks/useMainButton';
import { LoadingSpinner, ErrorMessage } from '../components/Feedback';
import { FireIcon, EyeIcon } from '../components/icons';
import { getProjectIconAsset } from '../data/projectIcons';
import { haptic } from '../utils/haptic';

const REACTION_TYPES = [
  { type: 'want', label: 'Хочу такое', Icon: FireIcon },
  { type: 'idea', label: 'Классная идея', Icon: IdeaIcon },
  { type: 'invest', label: 'Инвестирую', Icon: HandshakeIcon },
] as const;
const GITHUB_REPO_RE = /^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/;

function updateShowcaseReactionCounts(
  cached: InfiniteData<ShowcaseProjectsResponse> | undefined,
  projectId: string,
  reactionType: PublicProject['viewer_reaction_type'],
): InfiniteData<ShowcaseProjectsResponse> | undefined {
  if (!cached || !reactionType) return cached;

  return {
    ...cached,
    pages: cached.pages.map((page) => ({
      ...page,
      items: page.items.map((item) => {
        if (item.id !== projectId) return item;
        return {
          ...item,
          reactions_count: Math.max(0, (item.reactions_count ?? 0) + 1),
          want_count: reactionType === 'want' ? Math.max(0, (item.want_count ?? 0) + 1) : (item.want_count ?? 0),
          idea_count: reactionType === 'idea' ? Math.max(0, (item.idea_count ?? 0) + 1) : (item.idea_count ?? 0),
          invest_count: reactionType === 'invest' ? Math.max(0, (item.invest_count ?? 0) + 1) : (item.invest_count ?? 0),
        };
      }),
    })),
  };
}

function IdeaIcon({ size = 14 }: { size?: number }) {
  return (
    <svg viewBox="0 0 18 18" fill="none" width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx="9" cy="7.5" r="4.5" fill="rgba(251,191,36,0.15)" stroke="#fbbf24" strokeWidth="1.2" />
      <path d="M6.5 12H11.5" stroke="#fbbf24" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M7 13.5H11" stroke="#fbbf24" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M9 3V2" stroke="#fbbf24" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M13 5L13.7 4.3" stroke="#fbbf24" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M5 5L4.3 4.3" stroke="#fbbf24" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function HandshakeIcon({ size = 14 }: { size?: number }) {
  return (
    <svg viewBox="0 0 18 18" fill="none" width={size} height={size} style={{ flexShrink: 0 }}>
      <path
        d="M2 7L6 5L9 7L12 5L16 7"
        stroke="#38bdf8"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 7V13M16 7V13"
        stroke="#38bdf8"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <path
        d="M2 13C4 13 5 11.5 7 11.5C9 11.5 9 13 11 13C13 13 14 11.5 16 13"
        stroke="#38bdf8"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <path d="M6.5 9L9 7.5L11.5 9" stroke="#38bdf8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ProjectSvgIcon({ emoji }: { emoji?: string | null }) {
  const icon = getProjectIconAsset(emoji);
  return (
    <div style={{
      width: 56,
      height: 56,
      borderRadius: 14,
      background: icon.bg,
      border: `1px solid ${icon.color}30`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}>
      <img
        src={icon.src}
        alt=""
        width={32}
        height={32}
        draggable={false}
        style={{ width: 32, height: 32, display: 'block', pointerEvents: 'none', userSelect: 'none' }}
      />
    </div>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" width={16} height={16} style={{ flexShrink: 0 }}>
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.2" />
      <path d="M8 4V8L10 10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function MoneyIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" width={16} height={16} style={{ flexShrink: 0 }}>
      <circle cx="8" cy="8" r="5" stroke="#34d399" strokeWidth="1.2" fill="rgba(52,211,153,0.08)" />
      <path d="M8 5V11M6 7H10" stroke="#34d399" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" width={16} height={16} style={{ flexShrink: 0 }}>
      <path d="M2 12V8H5V12H2ZM6 12V5H9V12H6ZM10 12V2H13V12H10Z" fill="#c084fc" opacity={0.8} />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" width={14} height={14} style={{ flexShrink: 0 }}>
      <rect x="2" y="4" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="1.2" />
      <path d="M2 5L8 9L14 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function hasUrlScheme(value: string): boolean {
  return /^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(value);
}

function normalizeLinkUrl(raw: string | null | undefined): string | null {
  const value = String(raw ?? '').trim();
  if (!value) return null;

  // Block relative links so Telegram WebApp doesn't resolve them against app host.
  if (
    value.startsWith('/') ||
    value.startsWith('./') ||
    value.startsWith('../') ||
    value.startsWith('#') ||
    value.startsWith('?')
  ) {
    return null;
  }

  let candidate = value;
  const lower = candidate.toLowerCase();

  if (candidate.startsWith('@')) {
    const username = candidate.slice(1).trim();
    if (!username) return null;
    candidate = `https://t.me/${username}`;
  } else if (candidate.startsWith('t.me/')) {
    candidate = `https://${candidate}`;
  } else if (lower.startsWith('github.com/')) {
    candidate = `https://${candidate}`;
  } else if (GITHUB_REPO_RE.test(candidate)) {
    candidate = `https://github.com/${candidate}`;
  } else if (candidate.startsWith('//')) {
    candidate = `https:${candidate}`;
  } else if (!hasUrlScheme(candidate)) {
    candidate = `https://${candidate}`;
  }

  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    if (!parsed.hostname) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

function isTelegramHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    return host === 't.me' || host.endsWith('.t.me');
  } catch {
    return false;
  }
}

function openProjectLink(rawUrl: string) {
  const absoluteUrl = normalizeLinkUrl(rawUrl);
  if (!absoluteUrl) return;

  const tg =
    typeof window !== 'undefined'
      ? (window as unknown as {
          Telegram?: { WebApp?: { openLink?: (u: string) => void; openTelegramLink?: (u: string) => void } };
        }).Telegram?.WebApp
      : undefined;

  if (isTelegramHttpUrl(absoluteUrl) && tg?.openTelegramLink) {
    tg.openTelegramLink(absoluteUrl);
    return;
  }

  if (tg?.openLink) {
    tg.openLink(absoluteUrl);
    return;
  }

  window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
}

function ProjectLink({
  href,
  label,
}: {
  href: string;
  label: string;
}) {
  return (
    <button
      type="button"
      className="detail-link-btn"
      onClick={() => openProjectLink(href)}
    >
      {label} →
    </button>
  );
}

/** Map GET /projects/{id} response to PublicProject-like shape for the same detail UI. */
function projectDetailsToPublicProject(d: ProjectDetails): PublicProject {
  const rawAnswers = (d as unknown as { answers?: Record<string, unknown> }).answers ?? {};
  const answers = typeof rawAnswers === 'object' && rawAnswers !== null
    ? (rawAnswers as Record<string, unknown>)
    : {};
  const rawFields = d.fields ?? {};
  const fields = typeof rawFields === 'object' && rawFields !== null
    ? (rawFields as Record<string, unknown>)
    : {};
  const f: Record<string, unknown> = { ...answers, ...fields };
  const pickText = (v: unknown): string | null => {
    if (v == null) return null;
    if (typeof v === 'string') {
      const s = v.trim();
      return s || null;
    }
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (typeof v === 'object') {
      const rec = v as Record<string, unknown>;
      for (const key of ['value', 'label', 'name', 'title', 'url']) {
        const raw = rec[key];
        if (typeof raw === 'string' && raw.trim()) return raw.trim();
      }
      return null;
    }
    return null;
  };
  const arr = (v: unknown, splitCommas = false): string[] => {
    if (typeof v === 'string') {
      const s = v.trim();
      if (!s) return [];
      if (!splitCommas) return [s];
      return s
        .replace(/;/g, ',')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }
    if (!Array.isArray(v)) return [];
    return v.flatMap((item) => {
      const text = pickText(item);
      if (!text) return [];
      if (!splitCommas) return [text];
      return text
        .replace(/;/g, ',')
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
    });
  };
  const str = (v: unknown): string | null => pickText(v);

  const tools = arr(f.tools, true);
  const statuses = arr(f.statuses, true);
  const audienceFromArray = arr(f.audience, true);
  const stackParts = [
    ...arr(f.stack_ai, true),
    ...arr(f.stack_tech, true),
    ...arr(f.stack_infra, true),
  ];
  const stack =
    str(f.stack) ??
    str(f.stack_reason) ??
    (stackParts.length > 0 ? stackParts.join(', ') : null) ??
    (tools.length > 0 ? tools.join(', ') : null);
  const links = arr(f.links);
  const projectStatus = str(f.project_status) ?? (statuses.find((status) => isProjectStageValue(status)) ?? null);
  const audienceType = str(f.audience_type) ?? (audienceFromArray.length > 0 ? audienceFromArray.join(', ') : null);
  const authorId = typeof d.author_id === 'number' ? d.author_id : null;

  const alwaysVisibleKeys = new Set([
    'project_title', 'title', 'problem', 'audience_type', 'audience', 'niche', 'what_done',
    'stack', 'stack_reason', 'tools', 'statuses', 'project_status', 'status', 'dev_time',
    'potential', 'goal', 'inbound_ready', 'cool_part', 'hardest_part', 'monetization',
    'author_name',
    'emoji', 'price_display', 'price', 'site_url', 'website_url', 'demo_url', 'github_url',
    'repo_url', 'tg_url', 'links',
  ]);
  const labelByKey: Record<string, string> = {
    project_subtitle: 'Подзаголовок',
    subtitle: 'Подзаголовок',
    category: 'Категория',
    author_role: 'Роль автора',
    role: 'Роль автора',
    stack_ai: 'AI в стеке',
    stack_tech: 'Tech в стеке',
    stack_infra: 'Инфраструктура',
    stack_reason: 'Комментарий по стеку',
  };
  const toLabel = (key: string): string =>
    labelByKey[key] ?? key.replace(/_/g, ' ').replace(/^\w/, (ch: string) => ch.toUpperCase());
  const extra_details: ProjectDetailRow[] = Object.entries(f)
    .filter(([key]) => !alwaysVisibleKeys.has(key))
    .map(([key, value]) => {
      const listValue = arr(value, true);
      const normalized = listValue.length > 0 ? listValue.join(', ') : str(value);
      return { label: toLabel(key), value: normalized ?? '' };
    })
    .filter((row) => row.value.trim().length > 0)
    .sort((a, b) => a.label.localeCompare(b.label, 'ru'));

  return {
    id: d.id,
    slug: d.public_slug ?? null,
    public_id: d.public_slug ?? d.id ?? '',
    public_url: '',
    published_at: d.published_at ?? null,
    show_contacts: d.show_contacts ?? false,
    cover_image_url: d.cover_image_url ?? null,
    title: str(f.project_title) ?? str((f as Record<string, unknown>).title) ?? 'Без названия',
    problem: str(f.problem) ?? null,
    audience_type: audienceType,
    niche: str(f.niche) ?? null,
    what_done: str(f.what_done) ?? null,
    stack,
    category: str(f.category) ?? null,
    project_status: projectStatus,
    dev_time: str(f.dev_time) ?? null,
    potential: str(f.potential) ?? null,
    goal: str(f.goal) ?? null,
    inbound_ready: str(f.inbound_ready) ?? null,
    cool_part: str(f.cool_part) ?? null,
    hardest_part: str(f.hardest_part) ?? null,
    monetization: str(f.monetization) ?? null,
    links,
    author_name: str(f.author_name) ?? null,
    contact_mode: str(f.author_contact_mode) ?? null,
    contact_value: str(f.author_contact_value) ?? str(f.author_contact) ?? null,
    is_owner: true,
    emoji: str(f.emoji) ?? null,
    statuses,
    views_count: typeof d.views_count === 'number' ? d.views_count : undefined,
    reactions_count: typeof d.reactions_count === 'number' ? d.reactions_count : undefined,
    want_count: typeof d.want_count === 'number' ? d.want_count : undefined,
    idea_count: typeof d.idea_count === 'number' ? d.idea_count : undefined,
    invest_count: typeof d.invest_count === 'number' ? d.invest_count : undefined,
    viewer_reaction_type: d.viewer_reaction_type ?? null,
    price: str(f.price_display) ?? str(f.price) ?? null,
    author: authorId ? { id: authorId, username: null, avatar_url: null } : null,
    site_url: str(f.site_url) ?? str(f.website_url) ?? null,
    demo_url: str(f.demo_url) ?? null,
    github_url: str(f.github_url) ?? str(f.repo_url) ?? null,
    tg_url: str(f.tg_url) ?? null,
    extra_details,
  };
}

export function ProjectDetailScreenContainer() {
  const queryClient = useQueryClient();
  const selectedProjectId = useAppStore((s) => s.selectedProjectId);
  const detailSource = useAppStore((s) => s.detailSource);
  const setScreen = useAppStore((s) => s.setScreen);
  const setSelectedProjectId = useAppStore((s) => s.setSelectedProjectId);
  const setDetailSource = useAppStore((s) => s.setDetailSource);
  const currentUser = useAppStore((s) => s.currentUser);

  const [project, setProject] = useState<PublicProject | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [fetchInfo, setFetchInfo] = useState<ApiErrorInfo | null>(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [reactionLoading, setReactionLoading] = useState(false);
  const [ownerStatus, setOwnerStatus] = useState<string | null>(null);
  const [unpublishing, setUnpublishing] = useState(false);
  const addNotification = useAppStore((s) => s.addNotification);
  const canModerateShowcaseProject = isModeratorOrAdmin(currentUser);

  const loadProject = useCallback(
    async (idOrSlug: string) => {
      setLoading(true);
      setError(null);
      setErrorDetails(null);
      setFetchInfo(null);
      setProject(null);
      const fromMy = detailSource === 'my';
      try {
        if (fromMy) {
          const data: ProjectDetails = await getProject(idOrSlug);
          let mapped: PublicProject;
          try {
            mapped = projectDetailsToPublicProject(data);
          } catch (mapErr) {
            setError('Не удалось отобразить проект');
            setErrorDetails(mapErr instanceof Error ? mapErr.message : String(mapErr));
            return;
          }
          setProject(mapped);
          setOwnerStatus(data.status ?? null);
        } else {
          setOwnerStatus(null);
          const data = await getPublicProject(idOrSlug);
          setProject(data);
          postProjectView(idOrSlug).catch(() => {});
        }
      } catch (err) {
        const info = getApiErrorInfo(err);
        const e = err instanceof Error ? err : new Error(String(err));
        setFetchInfo(info);
        setError(`Ошибка: ${e.message || info.message || 'Не удалось загрузить проект'}`);
        setErrorDetails(
          JSON.stringify(
            {
              status: info.status ?? null,
              message: info.message ?? null,
              request: info.request ?? null,
              error_report_id: info.errorReportRequestId ?? null,
            },
            null,
            2
          )
        );
      } finally {
        setLoading(false);
      }
    },
    [detailSource]
  );

  useEffect(() => {
    if (selectedProjectId) {
      loadProject(selectedProjectId);
    } else {
      setLoading(false);
      setProject(null);
      setError(null);
      setOwnerStatus(null);
    }
  }, [selectedProjectId, loadProject]);

  const onBack = useCallback(() => {
    setScreen('list');
    setSelectedProjectId(null);
    setDetailSource(null);
  }, [setScreen, setSelectedProjectId, setDetailSource]);

  const onRetry = useCallback(() => {
    if (selectedProjectId) loadProject(selectedProjectId);
  }, [selectedProjectId, loadProject]);

  const [withdrawing, setWithdrawing] = useState(false);
  const handleWithdraw = useCallback(async () => {
    if (!selectedProjectId || withdrawing) return;
    setWithdrawing(true);
    try {
      await withdrawProject(selectedProjectId);
      addNotification('Проект отозван');
      await loadProject(selectedProjectId);
    } catch {
      addNotification('Не удалось отозвать проект');
    } finally {
      setWithdrawing(false);
    }
  }, [selectedProjectId, loadProject, addNotification, withdrawing]);

  const handleReact = useCallback(async (projectId: string, type: string) => {
    if (!project || project.is_owner || project.viewer_reaction_type || reactionLoading) return;
    setReactionLoading(true);
    try {
      await reactToProject(projectId, type);
      const reactionType = type as PublicProject['viewer_reaction_type'];
      setProject((prev) => (
        prev && prev.id === projectId
          ? {
              ...prev,
              viewer_reaction_type: reactionType,
              reactions_count: Math.max(0, (prev.reactions_count ?? 0) + 1),
              want_count: type === 'want' ? Math.max(0, (prev.want_count ?? 0) + 1) : (prev.want_count ?? 0),
              idea_count: type === 'idea' ? Math.max(0, (prev.idea_count ?? 0) + 1) : (prev.idea_count ?? 0),
              invest_count: type === 'invest' ? Math.max(0, (prev.invest_count ?? 0) + 1) : (prev.invest_count ?? 0),
            }
          : prev
      ));
      queryClient.setQueriesData<InfiniteData<ShowcaseProjectsResponse>>(
        { queryKey: ['showcase'] },
        (cached) => updateShowcaseReactionCounts(cached, projectId, reactionType),
      );
      void queryClient.invalidateQueries({ queryKey: ['showcase'] });
      addNotification('Спасибо за реакцию!');
    } catch {
      addNotification('Не удалось сохранить реакцию');
    } finally {
      setReactionLoading(false);
    }
  }, [addNotification, project, queryClient, reactionLoading]);

  const handleContactClick = useCallback(async (projectId: string, knownUsername?: string | null) => {
    const openTg = (username: string) => {
      const url = `https://t.me/${username}`;
      const tg = typeof window !== 'undefined' ? (window as unknown as { Telegram?: { WebApp?: { openTelegramLink?: (url: string) => void } } }).Telegram?.WebApp : undefined;
      if (tg?.openTelegramLink) {
        tg.openTelegramLink(url);
      } else {
        navigator.clipboard.writeText(url).then(
          () => useAppStore.getState().addNotification('Ссылка скопирована: t.me/' + username),
          () => window.open(url, '_blank', 'noopener,noreferrer')
        );
      }
    };
    if (knownUsername?.trim()) {
      openTg(knownUsername.trim());
      return;
    }
    setContactLoading(true);
    try {
      const res = await postProjectContactClick(projectId);
      const username = res?.owner_contact?.username;
      if (username) openTg(username);
      else useAppStore.getState().addNotification('Контакты автора не указаны');
    } catch {
      useAppStore.getState().addNotification('Контакты автора не указаны');
    } finally {
      setContactLoading(false);
    }
  }, []);

  const handleAdminUnpublish = useCallback(async () => {
    if (!selectedProjectId || unpublishing) return;
    setUnpublishing(true);
    try {
      await adminUnpublishProject(selectedProjectId);
      await queryClient.invalidateQueries({ queryKey: ['showcase'] });
      addNotification('Проект снят с витрины');
      await loadProject(selectedProjectId);
    } catch {
      addNotification('Не удалось снять проект с витрины');
    } finally {
      setUnpublishing(false);
    }
  }, [selectedProjectId, unpublishing, queryClient, addNotification, loadProject]);

  return (
    <ProjectDetailScreen
      project={project}
      loading={loading}
      error={error}
      errorDetails={errorDetails}
      fetchInfo={fetchInfo}
      contactLoading={contactLoading}
      reactionLoading={reactionLoading}
      ownerStatus={ownerStatus}
      onWithdraw={handleWithdraw}
      withdrawing={withdrawing}
      canAdminUnpublish={canModerateShowcaseProject && project?.is_owner !== true}
      onAdminUnpublish={handleAdminUnpublish}
      unpublishing={unpublishing}
      onBack={onBack}
      onRetry={onRetry}
      onReact={handleReact}
      onContactClick={handleContactClick}
    />
  );
}

export function ProjectDetailScreen({
  project,
  loading,
  error,
  errorDetails,
  fetchInfo,
  contactLoading,
  reactionLoading,
  ownerStatus = null,
  onWithdraw,
  withdrawing = false,
  canAdminUnpublish = false,
  onAdminUnpublish,
  unpublishing = false,
  onBack,
  onRetry,
  onReact,
  onContactClick,
}: {
  project: PublicProject | null;
  loading: boolean;
  error: string | null;
  errorDetails: string | null;
  fetchInfo?: ApiErrorInfo | null;
  contactLoading: boolean;
  reactionLoading: boolean;
  ownerStatus?: string | null;
  onWithdraw?: () => void;
  withdrawing?: boolean;
  canAdminUnpublish?: boolean;
  onAdminUnpublish?: () => void;
  unpublishing?: boolean;
  onBack: () => void;
  onRetry: () => void;
  onReact: (projectId: string, type: string) => void;
  onContactClick: (projectId: string, knownUsername?: string | null) => void;
}) {
  useBackButton(onBack, true);
  useDisableSwipes(true);

  const statuses = project?.statuses ?? [];
  const displayStatuses = statuses
    .map((status) => getDisplayProjectMetaLabel(status) ?? status)
    .filter((status): status is string => Boolean(status));
  const displayProjectStatus = getProjectStageLabel(
    project?.project_status ?? (statuses.find((status) => isProjectStageValue(status)) ?? null)
  );
  const stackList =
    project?.stack && String(project.stack)
      ? String(project.stack)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  const isOwner = project?.is_owner === true;
  const activeReaction = project?.viewer_reaction_type ?? null;
  const reactionCounts = {
    want: Math.max(0, project?.want_count ?? 0),
    idea: Math.max(0, project?.idea_count ?? 0),
    invest: Math.max(0, project?.invest_count ?? 0),
  };
  const authorUsername =
    project?.author?.username ??
    (project?.contact_value
      ? (String(project.contact_value).replace(/^@/, '').trim() ||
          project.contact_value.match(/t\.me\/([a-zA-Z0-9_]+)/)?.[1] ||
        null)
      : null);
  const setScreen = useAppStore((s) => s.setScreen);
  const setViewProfileUserId = useAppStore((s) => s.setViewProfileUserId);
  const siteUrl = normalizeLinkUrl(project?.site_url || project?.website_url);
  const demoUrl = normalizeLinkUrl(project?.demo_url);
  const githubUrl = normalizeLinkUrl(project?.github_url || project?.repo_url);
  const tgUrl = normalizeLinkUrl(project?.tg_url);
  const rawLinks = Array.isArray(project?.links) ? project.links : [];
  const normalizedGenericLinks = rawLinks
    .map((link) => normalizeLinkUrl(link))
    .filter((url): url is string => Boolean(url));
  const typedLinks = [siteUrl, demoUrl, githubUrl, tgUrl].filter((url): url is string => Boolean(url));
  const typedLinksSet = new Set(typedLinks);
  const uniqueGenericLinks = normalizedGenericLinks.filter(
    (url, idx, list) => !typedLinksSet.has(url) && list.indexOf(url) === idx
  );
  const hasProjectLinks = Boolean(siteUrl || demoUrl || githubUrl || tgUrl || uniqueGenericLinks.length > 0);
  const baseDetailsRows = [
    { label: 'Аудитория', value: project?.audience_type ?? null },
    { label: 'Ниша', value: project?.niche ?? null },
    { label: 'Категория', value: project?.category ?? null },
    { label: 'Стадия', value: displayProjectStatus },
    { label: 'Цель', value: project?.goal ?? null },
    { label: 'Потенциал', value: project?.potential ?? null },
    { label: 'Монетизация', value: project?.monetization ?? null },
    { label: 'Готовность к входящим', value: project?.inbound_ready ?? null },
  ];
  const extraDetailsRows = Array.isArray(project?.extra_details)
    ? project.extra_details.map((row) => ({ label: row.label, value: row.value }))
    : [];
  const detailsRows = [...baseDetailsRows, ...extraDetailsRows]
    .filter((row) => Boolean(row.value && String(row.value).trim()))
    .filter((row, idx, rows) => rows.findIndex((item) => item.label === row.label && item.value === row.value) === idx);
  const statusMetricValue = displayProjectStatus ?? 'Не указано';
  const canOwnerWithdraw = isOwner && ['submitted', 'approved', 'published'].includes(String(ownerStatus || '').toLowerCase());

  const handleContact = useCallback(() => {
    if (isOwner || !project) return;
    haptic.action();
    onContactClick(project.id, authorUsername);
  }, [isOwner, project, authorUsername, onContactClick]);

  const handleAuthorClick = useCallback(() => {
    if (!project?.author?.id) return;
    haptic.tap();
    setViewProfileUserId(project.author.id);
    setScreen('user-profile');
  }, [project?.author?.id, setScreen, setViewProfileUserId]);

  useMainButton({
    text: 'Написать автору',
    onClick: handleContact,
    visible: !isOwner && !!authorUsername,
  });

  if (loading) {
    return (
      <div className="detail-screen detail-screen--showcase">
        <header className="detail-header-bar">
          <div className="detail-header-spacer" aria-hidden />
          <h1 className="detail-header-title">Карточка проекта</h1>
        </header>
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="detail-screen detail-screen--showcase">
        <header className="detail-header-bar">
          <div className="detail-header-spacer" aria-hidden />
          <h1 className="detail-header-title">Карточка проекта</h1>
        </header>
        <ErrorMessage
          message={error || 'Проект не найден'}
          details={errorDetails}
          fetchInfo={fetchInfo}
          onRetry={onRetry}
        />
      </div>
    );
  }

  const relativeTime = (project?.published_at != null && project.published_at !== '')
    ? formatRelativeTime(project.published_at)
    : null;

  return (
    <div className="detail-screen detail-screen--showcase">
      <header className="detail-header-bar">
        <div className="detail-header-spacer" aria-hidden />
        <div className="detail-header-titles">
          <h1 className="detail-header-title">Карточка проекта</h1>
          <p className="detail-header-subtitle">{project.title || project.niche || project.audience_type || ''}</p>
        </div>
        <div className="detail-header-spacer" aria-hidden />
      </header>

      <div className="detail-scroll">
        <section className="detail-hero detail-hero--row">
          <span className="detail-hero-emoji detail-hero-emoji--square" aria-hidden>
            {project?.cover_image_url ? (
              <img src={project.cover_image_url} alt="" className="detail-hero-cover" width={56} height={56} />
            ) : (
              <ProjectSvgIcon emoji={project?.emoji} />
            )}
          </span>
          <div className="detail-hero-body">
            <h2 className="detail-hero-title">{project?.title ?? ''}</h2>
            <div className="detail-hero-author">
              {project?.author?.avatar_url ? (
                <img
                  src={project.author.avatar_url}
                  alt=""
                  className="detail-hero-avatar"
                  width={24}
                  height={24}
                />
              ) : null}
              {project?.author?.id ? (
                <button
                  type="button"
                  className="detail-hero-author-name detail-hero-author-name--clickable"
                  onClick={handleAuthorClick}
                >
                  {project?.author?.username ? `@${project.author.username}` : (project?.author_name ?? 'Автор')}
                  {relativeTime ? ` · ${relativeTime}` : ''}
                </button>
              ) : (
                <span className="detail-hero-author-name">
                  {project?.author?.username ? `@${project.author.username}` : (project?.author_name ?? 'Автор')}
                  {relativeTime ? ` · ${relativeTime}` : ''}
                </span>
              )}
            </div>
            {displayStatuses.length > 0 && (
              <div className="detail-hero-badges">
                {displayStatuses.map((status, index) => (
                  <span key={`${status}-${index}`} className="detail-hero-badge">
                    {status}
                  </span>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="detail-metrics">
          <h3 className="detail-metrics-label">МЕТРИКИ</h3>
          <div className="detail-metrics-grid">
            <div className="detail-metric">
              <span className="detail-metric-icon"><ClockIcon /></span>
              <div className="detail-metric-content">
                <span className="detail-metric-name">Разработка</span>
                <span className="detail-metric-value detail-metric-value--purple">
                  {project?.dev_time ?? 'Не указано'}
                </span>
              </div>
            </div>
            <div className="detail-metric">
              <span className="detail-metric-icon"><MoneyIcon /></span>
              <div className="detail-metric-content">
                <span className="detail-metric-name">Цена</span>
                <span className="detail-metric-value detail-metric-value--green">
                  {project?.price ?? 'Не указано'}
                </span>
              </div>
            </div>
            <div className="detail-metric">
                <span className="detail-metric-icon"><ChartIcon /></span>
              <div className="detail-metric-content">
                <span className="detail-metric-name">Стадия</span>
                <span className="detail-metric-value detail-metric-value--muted">
                  {statusMetricValue}
                </span>
              </div>
            </div>
            <div className="detail-metric">
              <span className="detail-metric-icon"><EyeIcon /></span>
              <div className="detail-metric-content">
                <span className="detail-metric-name">Просмотры</span>
                <span className="detail-metric-value detail-metric-value--orange">
                  {project?.views_count != null ? String(project.views_count) : 'Не указано'}
                </span>
              </div>
            </div>
          </div>
        </section>

        {project?.problem ? (
          <section className="detail-block">
            <h3 className="detail-block-label">ПРОБЛЕМА</h3>
            <p className="detail-block-text">{project.problem}</p>
          </section>
        ) : null}

        {project?.what_done ? (
          <section className="detail-block">
            <h3 className="detail-block-label">РЕШЕНИЕ</h3>
            <p className="detail-block-text">{project.what_done}</p>
          </section>
        ) : null}

        {detailsRows.length > 0 ? (
          <section className="detail-block">
            <h3 className="detail-block-label">ДЕТАЛИ ПРОЕКТА</h3>
            <div>
              {detailsRows.map((row) => (
                <p key={row.label} className="detail-block-text" style={{ marginBottom: '0.35rem' }}>
                  <strong>{row.label}:</strong> {row.value}
                </p>
              ))}
            </div>
          </section>
        ) : null}

        {stackList.length > 0 ? (
          <section className="detail-block">
            <h3 className="detail-block-label">СТЕК ИНСТРУМЕНТОВ</h3>
            <div className="detail-stack">
              {stackList.map((t) => (
                <span key={t} className="detail-stack-badge">
                  {t}
                </span>
              ))}
            </div>
          </section>
        ) : null}

        {project?.cool_part ? (
          <section className="detail-block">
            <h3 className="detail-block-label">СИЛЬНАЯ СТОРОНА</h3>
            <p className="detail-block-text">{project.cool_part}</p>
          </section>
        ) : null}

        {project?.hardest_part ? (
          <section className="detail-block">
            <h3 className="detail-block-label">СЛОЖНОСТЬ</h3>
            <p className="detail-block-text">{project.hardest_part}</p>
          </section>
        ) : null}

        {hasProjectLinks ? (
          <section className="detail-block">
            <h3 className="detail-block-label">ССЫЛКИ</h3>
            <div className="detail-links">
              {siteUrl && (
                <ProjectLink href={siteUrl} label="Сайт" />
              )}
              {demoUrl && (
                <ProjectLink href={demoUrl} label="Демо" />
              )}
              {githubUrl && (
                <ProjectLink href={githubUrl} label="GitHub" />
              )}
              {tgUrl && (
                <ProjectLink href={tgUrl} label="Telegram" />
              )}
              {uniqueGenericLinks.map((link, index) => (
                <ProjectLink
                  key={link}
                  href={link}
                  label={`Ссылка ${index + 1}`}
                />
              ))}
            </div>
          </section>
        ) : null}

        <div className="detail-spacer" aria-hidden />
      </div>

      <footer className="detail-cta-fixed">
        <div className="detail-reactions-bar">
          {REACTION_TYPES.map(({ type, label, Icon: ReactionIcon }) => (
            <button
              key={type}
              type="button"
              className={`detail-reaction-btn ${activeReaction === type ? 'detail-reaction-btn--active' : ''}`}
              onClick={() => {
                if (isOwner || activeReaction !== null || reactionLoading) return;
                haptic.action();
                onReact(project.id, type);
              }}
              disabled={isOwner || activeReaction !== null || reactionLoading}
            >
              <span className="detail-reaction-emoji"><ReactionIcon size={14} /></span>
              {reactionCounts[type] > 0 ? <span className="detail-reaction-count">{reactionCounts[type]}</span> : null}
              <span className="detail-reaction-label">{label}</span>
            </button>
          ))}
        </div>
        {canOwnerWithdraw && onWithdraw && (
          <button
            type="button"
            className="detail-action-btn"
            disabled={withdrawing}
            onClick={() => { haptic.tap(); onWithdraw(); }}
            style={{ marginBottom: '0.5rem' }}
          >
            {withdrawing ? '…' : 'Вернуть в черновик'}
          </button>
        )}
        {canAdminUnpublish && onAdminUnpublish && (
          <button
            type="button"
            className="detail-action-btn detail-action-btn--danger"
            disabled={unpublishing}
            onClick={() => { haptic.tap(); onAdminUnpublish(); }}
            style={{ marginBottom: '0.5rem' }}
          >
            {unpublishing ? '…' : 'Снять с витрины'}
          </button>
        )}
        {(!authorUsername || isOwner) && (
          <button
            type="button"
            className="detail-cta-btn"
            disabled={isOwner || contactLoading}
            onClick={() => { haptic.tap(); if (!isOwner && project) onContactClick(project.id, authorUsername); }}
          >
            {contactLoading ? '…' : isOwner ? 'Это ваш проект' : <><MailIcon /> Написать автору</>}
          </button>
        )}
      </footer>
    </div>
  );
}

function formatRelativeTime(iso: string | null): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
    if (days === 0) return 'сегодня';
    if (days === 1) return '1 день назад';
    if (days >= 2 && days <= 4) return `${days} дня назад`;
    if (days >= 5 && days <= 20) return `${days} дней назад`;
    if (days === 21) return '21 день назад';
    if (days >= 22 && days <= 30) return `${days} дня назад`;
    return `${days} дней назад`;
  } catch {
    return null;
  }
}
